import { IContextualMenuItem, IContextualMenuListProps } from '@fluentui/react';
import {
  IPButtonProps,
  IPContextualMenuItemType,
  IPContextualMenuProps,
  IPRenderFunction,
  IPSearchBox,
  IPStack,
  IPText,
} from 'baseComponents';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

//
// Adds a filter box on top of a long contextual menu, in the shape of the library Add menu.
//
// A call site opts in by spreading getSearchableMenuProps() into the menu props it already builds.
// The filter state lives inside the render function, so no caller needs state of its own, and the
// caller keeps handing over the complete item list.
//
// Filtering here rather than at the call site is deliberate: ContextualMenu renders null when its
// items are empty, so a caller that filtered its own items would tear the whole callout down as
// soon as a query matched nothing. It also computes the checkmark and icon gutters from the items
// it is given, so those would jump around while typing.
//

export interface ISearchableMenuOptions {
  /** Keys of commands the filter must never remove, such as 'add new' or 'manage'. */
  pinnedKeys?: string[];
  /** Only show the filter box from this many filterable items onwards. */
  minItemsForSearch?: number;
  /** Max height of the scrolling item list below the filter box. */
  maxListHeight?: number;
  /** Min width of the menu while the filter box shows. */
  minWidth?: number;
}

const defaultMinItemsForSearch = 8;
const defaultMaxListHeight = 360;
const defaultMinWidth = 300;

// The clear button sits right after the input in the DOM, so it would otherwise swallow the
// arrow-down that is meant to move focus from the filter box into the list. IButtonProps does not
// declare data attributes, hence the assertion.
const clearButtonNotFocusable = { 'data-is-focusable': false } as IPButtonProps;

interface ISearchableMenuListProps {
  options?: ISearchableMenuOptions;
  menuListProps?: IContextualMenuListProps;
  defaultRender?: IPRenderFunction<IContextualMenuListProps>;
}

//
// An item is only hidden by the filter when it is a normal item with a text of its own.
// Dividers, headers and items that render their own content (spinners, empty states, links)
// always pass through.
//
const isFilterable = (item: IContextualMenuItem, pinnedKeys: string[]): boolean => {
  if (pinnedKeys.includes(item.key)) return false;

  if (item.itemType !== undefined && item.itemType !== IPContextualMenuItemType.Normal) return false;

  return item.text !== undefined;
};

const matchesQuery = (item: IContextualMenuItem, query: string): boolean => {
  if (item.text && item.text.toLowerCase().includes(query)) return true;

  return item.secondaryText !== undefined && item.secondaryText.toLowerCase().includes(query);
};

//
// Filtering leaves dividers behind that no longer separate anything. Drop the ones that ended up
// leading, trailing or next to another divider, so a pinned command group stays tidy whether it
// sits above or below the list.
//
const removeDanglingDividers = (items: IContextualMenuItem[]): IContextualMenuItem[] => {
  const isDivider = (item: IContextualMenuItem) => item.itemType === IPContextualMenuItemType.Divider;

  return items.filter((item, idx) => {
    if (!isDivider(item)) return true;

    const hasContentBefore = items.slice(0, idx).some((i) => !isDivider(i));
    const hasContentAfter = items.slice(idx + 1).some((i) => !isDivider(i));
    const isFirstOfRun = idx === 0 || !isDivider(items[idx - 1]);

    return hasContentBefore && hasContentAfter && isFirstOfRun;
  });
};

//
// ContextualMenu counts items for aria-setsize the same way: dividers and headers do not count.
//
const countMenuItems = (items: IContextualMenuItem[]): number =>
  items.filter(
    (item) => item.itemType !== IPContextualMenuItemType.Divider && item.itemType !== IPContextualMenuItemType.Header,
  ).length;

//
// Declared at module level on purpose: a component created inside getSearchableMenuProps would be a
// new element type on every call, which remounts it and loses the query on every parent render.
//
const SearchableMenuList = (props: ISearchableMenuListProps) => {
  const { t } = useTranslation(['translation']);
  const [query, setQuery] = useState<string>('');

  const items = props.menuListProps?.items ?? [];
  const pinnedKeys = props.options?.pinnedKeys ?? [];
  const minItemsForSearch = props.options?.minItemsForSearch ?? defaultMinItemsForSearch;
  const maxListHeight = props.options?.maxListHeight ?? defaultMaxListHeight;
  const minWidth = props.options?.minWidth ?? defaultMinWidth;

  // Counted on the unfiltered list, which is what this render function always receives,
  // so the filter box cannot disappear halfway through typing.
  const showSearch = items.filter((item) => isFilterable(item, pinnedKeys)).length >= minItemsForSearch;

  const renderItems = (): IContextualMenuItem[] => {
    const lcQuery = query.trim().toLowerCase();
    if (!showSearch || !lcQuery) return items;

    const matched = items.filter((item) => !isFilterable(item, pinnedKeys) || matchesQuery(item, lcQuery));

    return removeDanglingDividers(matched);
  };

  const filteredItems = renderItems();
  const hasResults = filteredItems.some((item) => isFilterable(item, pinnedKeys));

  const renderList = () => {
    if (!props.defaultRender || !props.menuListProps) return null;

    return props.defaultRender({
      ...props.menuListProps,
      items: filteredItems,
      totalItemCount: countMenuItems(filteredItems),
    });
  };

  if (!showSearch) {
    return renderList();
  }

  return (
    <IPStack styles={{ root: { minWidth: minWidth } }}>
      <IPStack.Item styles={{ root: { padding: '8px 8px 4px 8px' } }}>
        <IPSearchBox
          placeholder={t('translation:General.Filter.Placeholder')}
          value={query}
          clearButtonProps={clearButtonNotFocusable}
          onChange={(_ev, newValue) => setQuery(newValue ?? '')}
          onClick={(ev) => ev.stopPropagation()}
        />
      </IPStack.Item>
      <IPStack.Item styles={{ root: { maxHeight: `min(${maxListHeight}px, 45vh)`, overflowY: 'auto' } }}>
        {renderList()}
      </IPStack.Item>
      {!hasResults && (
        <IPStack.Item styles={{ root: { padding: '4px 8px 8px 12px' } }}>
          <IPText>{t('translation:General.Suggestions.NoData')}</IPText>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

//
// Spread the result into the menu props. All three properties are needed together:
// - shouldFocusOnContainer, or a mouse or hover open focuses the menu container instead of the box
// - shouldInputLoseFocusOnArrowKey, or arrow-down keeps the focus trapped in the box
//
export const getSearchableMenuProps = (options?: ISearchableMenuOptions): Partial<IPContextualMenuProps> => {
  return {
    shouldFocusOnContainer: false,
    focusZoneProps: { shouldInputLoseFocusOnArrowKey: () => true },
    onRenderMenuList: (menuListProps, defaultRender) => (
      <SearchableMenuList options={options} menuListProps={menuListProps} defaultRender={defaultRender} />
    ),
  };
};
